import { useEffect, useMemo, useState } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type CacheAnalyticsCallback,
	type DegradationCallback,
	get3pSearchConfig,
	getSearchConfigV2,
	getUserLoomAuthStatus,
	type LinkedSite,
	type TSearch3pConfigProps,
	type TSearchConfigAPIResponse,
} from '@atlassian/search-client';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import {
	getCombinedSearchConfig,
	seedCombinedConfigCacheEntry,
} from '@atlassian/search-client/config';
import { searchLinkedSitesQuery } from '@atlassian/search-client/search-page';

import { type ProductKeys3P } from '../../common/constants/3p-product-configs';
import { EVENT_SOURCE } from '../../common/constants/analytics';
import { type NounKeys, nouns as supportedNouns } from '../../common/constants/nouns';
import {
	type PrimaryProductKey,
	PrimaryProductKeys,
	ProductKeys,
	useProductConfigs,
} from '../../common/constants/products';
import {
	type ForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../common/constants/schemas/3p-config';
import { SearchExperience, type SearchStoreExperience } from '../../common/constants/search';
import { type SearchCombinedConfigPayload } from '../../common/types/search-combined-config';
import { type SearchPreloadDataProvider } from '../../common/types/search-preload';
import { onDegradedDependency } from '../../common/utils/events/degraded-dependency';
import { onFailedDependency } from '../../common/utils/events/failed-dependency';
import { onRequestCacheAccess } from '../../common/utils/events/on-cache-access';
import {
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
	TASK_NAMES,
} from '../../common/utils/events/vital-stats-events';
import { serveSearchCombinedConfigExperiment } from '../../common/utils/experiments';
import { createVitalStatsNetworkCallbacks } from '../../common/utils/network-task-tracker';
import { useAnalytics } from '../analytics';
import { useLogException } from '../error-boundary/utils';
import { useSearchSessionId } from '../store/bootstrap';

import { adaptCombinedConfigToLegacy } from './combined-config-adapter';
import { parseCombinedConfig } from './utils/parse-combined-config';
import { map3pResourcesToProductKeys } from './utils/third-party-products';

const EXTRACT_PRODUCT = /ari:cloud:(.*)?::/;
const EXTRACT_CLOUD_ID = /ari:cloud:.*?\/(.*)/;
const BITBUCKET_WORKSPACE_ARI = 'ari:cloud:bitbucket::workspace';

export type UseFetchConfigProps = {
	thirdPartyAbsoluteUrl?: string;
	thirdPartyDisabled?: boolean;
	userId?: string | null;
	cloudId?: string;
	aggAbsoluteUrl?: string;
	primaryProduct?: PrimaryProductKey;
	/**
	 * When passed this would be used as is to invoke the endpoint which returns search-configuration i.e. list of products accessible by the logged in user.
	 * This is useful for products like Trello where the API gateway is not on the host domain, but a completely different URL.
	 *
	 * No need to pass if the UI is being bootstrapped inside Jira, Confluence or any product tied to these sites.
	 */
	configFetchAbsoluteUrl?: string;
	aggregatorHostName?: string;
	multiSiteSearchInRovo?: boolean;
	experience?: SearchStoreExperience;
	activationId?: string;
	preloadDataProvider?: SearchPreloadDataProvider;
};

const getFirstCloudId = (firstPartyResponse: TSearchConfigAPIResponse) => {
	const match = firstPartyResponse.resources[0]?.id.match(EXTRACT_CLOUD_ID);
	const extractedCloudId = match ? match[1] : '';

	return extractedCloudId;
};

const isNounsAvailable = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isNounsAvailable;
};

const isRovoEnabled = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isRovoEnabled;
};

const getJiraIssueIndexedForTenant = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isJiraIssueIndexed;
};

const getProductEdition = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productEdition,
	)?.productEdition;
};

const getProductDataRegions = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productDataRegions,
	)?.productDataRegions;
};

/**
 * Some products (eg. Loom) have separate auth checks that need to be enforced before
 * returning the final list of accessible products.
 *
 * This should only be used as a temporary solution, all auth checks should ideally be done
 * inside 1P and 3P configuration ednpoints.
 *
 * Remove when cleaning up fg('rovo-search-combined-loom-auth')
 */
const lastMileProductChecks = (isUserLoomAuthed: boolean, products?: ProductKeys[]) => {
	if (!products || products.length === 0) {
		return products;
	}

	return products.filter((product) => isUserLoomAuthed || product !== ProductKeys.Loom);
};

const getRovoFeatures = (firstPartyResponse?: TSearchConfigAPIResponse, cloudId?: string) => {
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.rovoFeatures,
	)?.rovoFeatures;
};

/**
 * A React hook which handles fetching of all the config necessary to bootstrap 1P + 3P results.
 */
export const useFetchConfig: ({
	cloudId,
	configFetchAbsoluteUrl,
	aggregatorHostName,
	userId,
	thirdPartyAbsoluteUrl,
	thirdPartyDisabled,
	primaryProduct,
	aggAbsoluteUrl,
	multiSiteSearchInRovo,
	experience,
	activationId,
	preloadDataProvider,
}: UseFetchConfigProps) => {
	accessibleProducts: string[] | undefined;
	accessibleNouns: NounKeys[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>> | undefined;
	firstCloudId: string;
	intercomHmac: string | undefined;
	isNounsAvailable: boolean | undefined;
	isRovoEnabled: boolean | undefined;
	isJiraIssueIndexed: boolean | undefined;
	productEdition: import('@atlassian/search-client').TProductEdition | undefined;
	productDataRegions: import('@atlassian/search-client').TProductDataRegions | undefined;
	rovoFeatures: import('@atlassian/search-client').TRovoFeatures | undefined;
	isConfigLoaded: boolean;
	linkedCloudARIs: string[] | undefined;
} = ({
	cloudId: cloudIdProp,
	configFetchAbsoluteUrl,
	aggregatorHostName,
	userId,
	thirdPartyAbsoluteUrl,
	thirdPartyDisabled,
	primaryProduct,
	aggAbsoluteUrl,
	multiSiteSearchInRovo,
	experience,
	activationId,
	preloadDataProvider,
}: UseFetchConfigProps) => {
	const supportedProducts = useProductConfigs();

	// Unnecessary, should be removed to save react cycles
	const [firstPartyResponse, setFirstPartyResponse] = useState<TSearchConfigAPIResponse>();
	const [isConfigLoaded, setIsConfigLoaded] = useState<boolean>(false);

	// Unnecessary, should be removed to save react cycles
	const [combinedThirdPartyResponse, setCombinedThirdPartyResponse] = useState<{
		productKeys?: (ProductKeys3P | ForgeConnectorProductKey)[];
		thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	}>({});

	// Loom requires a separate auth check, which is stored separately to `accessibleProductsConfig`.
	// This means Loom can be accessible through `accessibleProductsConfig`,
	// but unavailable to the user because of the auth check.
	// Clean up when removing fg('rovo-search-combined-loom-auth')
	const [isUserLoomAuthed, setIsUserLoomAuthed] = useState<boolean>(false);
	// Rovo Multiple Site Search
	const [linkedCloudARIs, setLinkedCloudARIs] = useState<string[] | undefined>(undefined);

	// Combined Config Response
	const [combinedConfigResponse, setCombinedConfigResponse] =
		useState<SearchCombinedConfigPayload>();

	// Unnecessary, should be removed to save react cycles
	const [accessibleProductsConfig, setAccessibleProductsConfig] = useState<{
		products?: ProductKeys[];
		nouns: NounKeys[];
		thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	}>({
		nouns: [],
	});
	const { fireAnalyticsEvent } = useAnalytics({ experience });
	const [searchSessionId] = useSearchSessionId();

	const logException = useLogException();

	const firstCloudId = useMemo(() => {
		if (!cloudIdProp && firstPartyResponse) {
			return getFirstCloudId(firstPartyResponse);
		}
		return '';
	}, [firstPartyResponse, cloudIdProp]);

	const thirdPartyResponseFrom3p = useThirdPartyConfig({
		cloudId: cloudIdProp || firstCloudId || '',
		userId: userId,
		absoluteUrl: thirdPartyAbsoluteUrl,
		disabled: thirdPartyDisabled || serveSearchCombinedConfigExperiment(),
		experience,
	});
	const thirdPartyResponse = serveSearchCombinedConfigExperiment()
		? combinedThirdPartyResponse
		: thirdPartyResponseFrom3p;

	const atlAttributionHeaders = useMemo(
		() =>
			buildAtlAttributionHeaders({
				cloudId: cloudIdProp,
				activationId,
				product: primaryProduct,
			}),
		[cloudIdProp, activationId, primaryProduct],
	);

	// Rovo Multiple Site Search - Store linked site ARIs in state
	useEffect(() => {
		const cloudId = cloudIdProp || firstCloudId;
		if (cloudId && multiSiteSearchInRovo) {
			searchLinkedSitesQuery({
				variables: { id: `ari:cloud:confluence::site/${cloudId}` },
				aggAbsoluteUrl,
				headers: atlAttributionHeaders,
			})
				.then((response) => {
					const edges = response?.data?.collabContext_workspaceIsConnectedToWorkspace?.edges ?? [];
					const linkedARIs = edges.map((edge: LinkedSite) => edge.node.id);

					// Move current cloudId to index 0 if it exists and isn't already there
					// This is a temporary solution for multiple site search in rovo with correct CloudIdARIs sequence
					const currentCloudIdARI = `ari:cloud:confluence::site/${cloudId}`;
					const currentIndex = linkedARIs.findIndex((ari) => ari === currentCloudIdARI);

					if (currentIndex > 0) {
						// Remove from current position and add to beginning
						linkedARIs.splice(currentIndex, 1);
						linkedARIs.unshift(currentCloudIdARI);
					}

					setLinkedCloudARIs(linkedARIs);
				})
				.catch((error) => {
					logException(error);
				});
		}
	}, [
		cloudIdProp,
		firstCloudId,
		multiSiteSearchInRovo,
		logException,
		aggAbsoluteUrl,
		atlAttributionHeaders,
	]);

	useEffect(() => {
		// this endpoint is working in confluence, atlas, jira and focus
		// check auth in above products, and directly show loom to other products for now
		// Remoe when cleaning up fg('rovo-search-combined-loom-auth')
		if (!fg('rovo-search-combined-loom-auth') && !isUserLoomAuthed) {
			if (
				primaryProduct === PrimaryProductKeys.Confluence ||
				primaryProduct === PrimaryProductKeys.Atlas ||
				((primaryProduct === PrimaryProductKeys.Jira ||
					primaryProduct === PrimaryProductKeys.Focus) &&
					fg('search_loom_auth_check_in_jira_mercury'))
			) {
				getUserLoomAuthStatus({
					userId,
					cloudId: cloudIdProp,
				})
					.then((response) => setIsUserLoomAuthed(response?.status === 'mastered'))
					.catch((error) => {
						logException(error);

						setIsUserLoomAuthed(false);
					});
			} else {
				setIsUserLoomAuthed(true);
			}
		}
	}, [isUserLoomAuthed, setIsUserLoomAuthed, userId, logException, cloudIdProp, primaryProduct]);

	useEffect(() => {
		if (serveSearchCombinedConfigExperiment() && !firstPartyResponse) {
			if (!cloudIdProp) {
				return;
			}

			if (combinedThirdPartyResponse.productKeys) {
				return;
			}

			/**
			 * Combined config caching strategy shared across both:
			 * - network fetch path (`getCombinedSearchConfig`)
			 * - SSR-preloaded network seed path (`seedCombinedConfigCacheEntry`)
			 *
			 * Strategy:
			 * - refresh only when cached data is expired
			 * - return cached value even if expired
			 */
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- existing search_combined_config experiment used for combined config cache TTL
			const combinedConfigTTL = FeatureGates.getExperimentValue<number>(
				'search_combined_config',
				'expiration',
				1000 * 60 * 1,
			);

			const fetchCombinedConfig = () => {
				const uxTaskId = crypto.randomUUID();
				const taskStartAt = performance.now();

				const baseUXVitalStatsParams = {
					searchSessionId,
					taskId: uxTaskId,
					task: TASK_NAMES.SEARCH_PAGE_COMBINED_CONFIG_UX,
				};

				// DegradedDependency may not be visible to the end-user due to request caching. This is a health metric for our BE
				const degradationCallback: DegradationCallback = (reason) => {
					fireAnalyticsEvent(
						onDegradedDependency({
							dependency: 'search-combined-config',
							searchSessionId,
							reason,
							source: EVENT_SOURCE,
						}),
					);
				};

				const cacheAnalyticsCallback: CacheAnalyticsCallback = async (state) => {
					fireAnalyticsEvent(
						onRequestCacheAccess({
							requestName: 'search-combined-config',
							searchSessionId,
							cacheHit: state.hit,
							refreshTriggered: state.refreshTriggered,
							source: EVENT_SOURCE,
						}),
					);
				};

				// Create network callbacks for tracking network requests
				const configNetworkTaskId = crypto.randomUUID();
				const networkCallbacks = createVitalStatsNetworkCallbacks(
					configNetworkTaskId,
					TASK_NAMES.SEARCH_PAGE_COMBINED_CONFIG_NETWORK,
					searchSessionId,
					fireAnalyticsEvent,
					taskStartAt,
					{ uxTaskId },
				);

				const searchConfigParams = {
					cloudId: cloudIdProp,
					userId,
					aggAbsoluteUrl,
					degradationCallback,
					cacheAnalyticsCallback,
					expiration: combinedConfigTTL,
					returnExpired: true,
					onlyRefreshIfExpired: true,
					networkCallbacks,
					headers: atlAttributionHeaders,
				};

				// Fire task start for UX tracking
				fireAnalyticsEvent(onTaskStart(baseUXVitalStatsParams));

				getCombinedSearchConfig(searchConfigParams)
					.then((configuration: SearchCombinedConfigPayload) => {
						setCombinedConfigResponse(configuration);
						const { firstPartyConfig, thirdPartyConfig } = adaptCombinedConfigToLegacy(
							configuration,
							cloudIdProp,
						);
						setFirstPartyResponse(firstPartyConfig);
						setCombinedThirdPartyResponse(map3pResourcesToProductKeys(thirdPartyConfig));

						// Fire user experience metric - user got data (success from UX perspective)
						fireAnalyticsEvent(
							onTaskSuccess({
								...baseUXVitalStatsParams,
								moreContext: {
									timeTaken: performance.now() - taskStartAt,
								},
							}),
						);
					})
					.catch((error: Error) => {
						logException(error);

						// FailedDependency is visible to the end-user. This is a health metric for our UX
						fireAnalyticsEvent(
							onFailedDependency({
								dependency: 'search-combined-config',
								searchSessionId,
								source: EVENT_SOURCE,
							}),
						);

						// Fire user experience metric - user did not get data (failure from UX perspective)
						fireAnalyticsEvent(
							onTaskFail({
								...baseUXVitalStatsParams,
								moreContext: {
									error: error.name,
									timeTaken: performance.now() - taskStartAt,
								},
							}),
						);

						setFirstPartyResponse({
							resources: [],
							intercomHmac: '',
							siteMetadata: [],
						});
						setCombinedThirdPartyResponse({
							productKeys: EMPTY_RESPONSE,
							thirdPartyConfigs: {},
						});
					});
			};

			if (preloadDataProvider) {
				try {
					const preloadedSearch = preloadDataProvider.getPreloadedSearch({
						cloudId: cloudIdProp,
					});
					if (preloadedSearch && preloadedSearch.config) {
						const preloadUxTaskId = crypto.randomUUID();
						const preloadTaskStartAt = performance.now();
						const preloadMoreContext = {
							isSSREvent: true,
							isConfigFromCache: Boolean(preloadedSearch.isConfigFromCache),
						};
						const preloadBaseUXVitalStatsParams = {
							searchSessionId,
							taskId: preloadUxTaskId,
							task: TASK_NAMES.SEARCH_PAGE_COMBINED_CONFIG_UX,
							moreContext: preloadMoreContext,
						};
						fireAnalyticsEvent(onTaskStart(preloadBaseUXVitalStatsParams));

						try {
							setCombinedConfigResponse(preloadedSearch.config);
							const { firstPartyConfig, thirdPartyConfig } = adaptCombinedConfigToLegacy(
								preloadedSearch.config,
								cloudIdProp,
							);
							setFirstPartyResponse(firstPartyConfig);
							setCombinedThirdPartyResponse(map3pResourcesToProductKeys(thirdPartyConfig));
							if (!preloadedSearch.isConfigFromCache) {
								void seedCombinedConfigCacheEntry({
									cloudId: cloudIdProp,
									userId,
									config: preloadedSearch.config,
									expiration: combinedConfigTTL,
								}).catch((seedError: unknown) => {
									logException(seedError);
								});
							}
							fireAnalyticsEvent(
								onTaskSuccess({
									...preloadBaseUXVitalStatsParams,
									moreContext: {
										...preloadMoreContext,
										timeTaken: performance.now() - preloadTaskStartAt,
									},
								}),
							);
							if (preloadedSearch.isConfigFromCache) {
								// Preload gives immediate UI data, but we still run the normal combined-config
								// path so cache TTL rules can trigger a network refresh when the entry is expired.
								fetchCombinedConfig();
							}
							return;
						} catch (error: unknown) {
							logException(error);
							fireAnalyticsEvent(
								onTaskFail({
									...preloadBaseUXVitalStatsParams,
									moreContext: {
										...preloadMoreContext,
										error: error instanceof Error ? error.name : 'unknown',
										timeTaken: performance.now() - preloadTaskStartAt,
									},
								}),
							);
							fetchCombinedConfig();
							return;
						}
					}
					if (preloadedSearch && !preloadedSearch.config) {
						fireAnalyticsEvent(
							onFailedDependency({
								dependency: 'search-config-ssr',
								searchSessionId,
								source: EVENT_SOURCE,
								moreContext: {
									cacheFailReason: preloadedSearch.cacheFailReason ?? 'none',
									configFailReason: preloadedSearch.configFailReason ?? 'none',
									preloadPayloadWithoutConfig: true,
								},
							}),
						);
					}
					fetchCombinedConfig();
				} catch (error: unknown) {
					logException(error);
					fetchCombinedConfig();
				}
				return;
			}

			fetchCombinedConfig();
		}
	}, [
		firstPartyResponse,
		combinedThirdPartyResponse.productKeys,
		cloudIdProp,
		userId,
		aggAbsoluteUrl,
		logException,
		fireAnalyticsEvent,
		searchSessionId,
		atlAttributionHeaders,
		preloadDataProvider,
	]);

	useEffect(() => {
		if (!serveSearchCombinedConfigExperiment() && !firstPartyResponse) {
			const uxTaskId = crypto.randomUUID();
			const taskStartAt = performance.now();

			const baseUXVitalStatsParams = {
				searchSessionId,
				taskId: uxTaskId,
				task: TASK_NAMES.SEARCH_PAGE_FIRST_PARTY_CONFIG_UX,
			};

			// DegradedDependency may not be visible to the end-user due to request caching. This is a health metric for our BE
			const degradationCallback: DegradationCallback = (reason) => {
				fireAnalyticsEvent(
					onDegradedDependency({
						dependency: 'search-config',
						searchSessionId,
						reason,
						source: EVENT_SOURCE,
					}),
				);
			};

			const cacheAnalyticsCallback: CacheAnalyticsCallback = async (state) => {
				fireAnalyticsEvent(
					onRequestCacheAccess({
						requestName: 'search-config',
						searchSessionId,
						cacheHit: state.hit,
						refreshTriggered: state.refreshTriggered,
						source: EVENT_SOURCE,
					}),
				);
			};

			/**
			 * These parameters define a caching strategy where:
			 *  - Cache is refreshed only if the cached data is expired, with a 15 minutes TTL
			 *  - Cached value is returned, regardless of whether its expired or not
			 */
			const search1PConfigTTL = fg('rovo_search_1p_config_increase_ttl')
				? 1000 * 60 * 15
				: 1000 * 60 * 1; // 15 minutes : 1 minute

			// Create network callbacks for tracking network requests
			const configNetworkTaskId = crypto.randomUUID();
			const networkCallbacks = createVitalStatsNetworkCallbacks(
				configNetworkTaskId,
				TASK_NAMES.SEARCH_PAGE_FIRST_PARTY_CONFIG_NETWORK,
				searchSessionId,
				fireAnalyticsEvent,
				taskStartAt,
				{ uxTaskId },
			);

			const searchConfigParams = {
				cloudId: cloudIdProp,
				absoluteUrl: configFetchAbsoluteUrl,
				hostName: aggregatorHostName,
				userId,
				degradationCallback,
				cacheAnalyticsCallback,
				expiration: search1PConfigTTL, // 15 minutes
				returnExpired: true,
				onlyRefreshIfExpired: true,
				networkCallbacks,
			};

			// Fire task start for UX tracking
			fireAnalyticsEvent(onTaskStart(baseUXVitalStatsParams));

			getSearchConfigV2(searchConfigParams)
				.then((response) => {
					setFirstPartyResponse(response);

					// Fire user experience metric - user got data (success from UX perspective)
					fireAnalyticsEvent(
						onTaskSuccess({
							...baseUXVitalStatsParams,
							moreContext: {
								timeTaken: performance.now() - taskStartAt,
							},
						}),
					);
				})
				.catch((error) => {
					logException(error);

					// FailedDependency is visible to the end-user. This is a health metric for our UX
					fireAnalyticsEvent(
						onFailedDependency({
							dependency: 'search-config',
							searchSessionId,
							source: EVENT_SOURCE,
						}),
					);

					// Fire user experience metric - user did not get data (failure from UX perspective)
					fireAnalyticsEvent(
						onTaskFail({
							...baseUXVitalStatsParams,
							moreContext: {
								error: error.name,
								timeTaken: performance.now() - taskStartAt,
							},
						}),
					);

					setFirstPartyResponse({
						resources: [],
						intercomHmac: '',
						siteMetadata: [],
					});
				});
		}
	}, [
		firstPartyResponse,
		setFirstPartyResponse,
		cloudIdProp,
		configFetchAbsoluteUrl,
		aggregatorHostName,
		userId,
		aggAbsoluteUrl,
		logException,
		fireAnalyticsEvent,
		searchSessionId,
	]);

	useEffect(() => {
		if (
			thirdPartyResponse.productKeys &&
			firstPartyResponse &&
			!accessibleProductsConfig.products
		) {
			let allAccessibleProducts: ProductKeys[] = [];

			const firstPartyProducts = [
				...new Set(
					firstPartyResponse.resources.flatMap((resource) => {
						const { id } = resource;
						const match = id.match(EXTRACT_PRODUCT);

						if (match && match.length >= 2 && match[1] in supportedProducts) {
							return [match[1]];
						}

						return [];
					}),
				),
			];

			if (firstPartyProducts.length === 0 && primaryProduct) {
				// Use the primary product if the 1P Search Config request fails
				allAccessibleProducts.push(primaryProduct as ProductKeys);
			} else {
				allAccessibleProducts.push(...(firstPartyProducts as ProductKeys[]));
			}

			// Always ensure the primary product is in the list, even if the config
			// response doesn't include it (e.g. resources only contain other products).
			// This mirrors the error fallback behavior where the primary product is
			// always available so users can search within their hosting product.
			if (
				primaryProduct &&
				!allAccessibleProducts.includes(primaryProduct as ProductKeys) &&
				fg('rovo-search-patch-primary-product')
			) {
				allAccessibleProducts.unshift(primaryProduct as ProductKeys);
			}

			// We are currently migrating Goal and Project nouns from townsquare (Atlas) into their own separate Products
			// within Identity. As part of this migration we are decommissioning the isNounsAvailable flag on siteMetadata
			// and moving the nouns into the first party product list.
			//
			// If a site has isNounsAvailable set to false then we want to return the available
			// nouns from the firstPartyResponse. If isNounsAvailable is true then the site hasn't been migrated yet and
			// should continue with the old behaviour.
			const areNounsEnabledThroughLegacyFlag = isNounsAvailable(firstPartyResponse, cloudIdProp);

			const accessibleNouns = areNounsEnabledThroughLegacyFlag
				? []
				: firstPartyResponse.resources.flatMap((resource) => {
						const { id } = resource;
						const match = id.match(EXTRACT_PRODUCT);

						if (match && match.length >= 2 && match[1] in supportedNouns) {
							return [match[1] as NounKeys];
						}

						return [];
					});

			// If nouns are available we also need to add Townsquare to the list of accessible products.
			// This way Projects will be fetched by default when no filters are selected.
			if (accessibleNouns.length > 0 && !areNounsEnabledThroughLegacyFlag) {
				allAccessibleProducts.push(ProductKeys.Atlas);
			}

			// with combined config, bitbucket is handled in the BE
			if (!serveSearchCombinedConfigExperiment()) {
				// Remove Bitbucket from the list of resources if:
				// * The user doesn't have access to the Bitbucket product
				// * There are no bitbucket workspaces
				// See https://hello.atlassian.net/wiki/x/1hI6CQE for details
				if (
					allAccessibleProducts.includes('bitbucket') &&
					!firstPartyResponse.resources.find((r) => r.id.includes(BITBUCKET_WORKSPACE_ARI))
				) {
					allAccessibleProducts = allAccessibleProducts.filter(
						(product) => product !== ProductKeys.Bitbucket,
					);
				}
			}

			// Allow 3P products only if a user has Rovo enabled.
			// Long-term it won't be needed if the 3p-configuration check ensures Rovo eligibility.
			// See https://hello.atlassian.net/wiki/spaces/SEARCH/pages/4469899124.
			const doesUserHaveRovo = isRovoEnabled(firstPartyResponse, cloudIdProp);
			if (doesUserHaveRovo) {
				const thirdPartyProductKeys = thirdPartyResponse.productKeys || [];
				allAccessibleProducts.push(...(thirdPartyProductKeys as ProductKeys[]));
				setAccessibleProductsConfig({
					products: allAccessibleProducts,
					nouns: accessibleNouns,
					thirdPartyConfigs: thirdPartyResponse.thirdPartyConfigs,
				});
			} else {
				setAccessibleProductsConfig({
					products: allAccessibleProducts,
					nouns: accessibleNouns,
					thirdPartyConfigs: {},
				});
			}
			setIsConfigLoaded(true);
		}
	}, [
		isUserLoomAuthed,
		thirdPartyResponse,
		firstPartyResponse,
		setAccessibleProductsConfig,
		primaryProduct,
		fireAnalyticsEvent,
		logException,
		cloudIdProp,
		accessibleProductsConfig.products,
		supportedProducts,
	]);

	const accessibleProducts = fg('rovo-search-combined-loom-auth')
		? accessibleProductsConfig.products
		: lastMileProductChecks(isUserLoomAuthed, accessibleProductsConfig.products);

	const parsedConfigFromNewCombinedConfigParser = useMemo(() => {
		if (
			primaryProduct &&
			cloudIdProp &&
			combinedConfigResponse &&
			fg('rovo_search_simplify_combined_config_parsing')
		) {
			const parsedCombinedConfig = parseCombinedConfig({
				configuration: combinedConfigResponse,
				primaryProduct: primaryProduct,
				cloudId: cloudIdProp,
			});

			return {
				...parsedCombinedConfig,
				firstCloudId: '', // cloudId must always exist when using the combined endpoint, hence we don't infer one
				isNounsAvailable: false, // xpsearch-aggregator never returns this metadata anymore
				isConfigLoaded,
				linkedCloudARIs,
			};
		}

		return null;
	}, [primaryProduct, cloudIdProp, combinedConfigResponse, isConfigLoaded, linkedCloudARIs]);

	if (parsedConfigFromNewCombinedConfigParser) {
		return parsedConfigFromNewCombinedConfigParser;
	}

	return {
		accessibleProducts,
		accessibleNouns: accessibleProductsConfig.nouns,
		thirdPartyConfigs: accessibleProductsConfig.thirdPartyConfigs,
		firstCloudId,
		intercomHmac: firstPartyResponse?.intercomHmac,
		isNounsAvailable: isNounsAvailable(firstPartyResponse, cloudIdProp),
		isRovoEnabled: isRovoEnabled(firstPartyResponse, cloudIdProp),
		isJiraIssueIndexed: getJiraIssueIndexedForTenant(firstPartyResponse, cloudIdProp),
		productEdition: getProductEdition(firstPartyResponse, cloudIdProp),
		productDataRegions: getProductDataRegions(firstPartyResponse, cloudIdProp),
		rovoFeatures: getRovoFeatures(firstPartyResponse, cloudIdProp),
		isConfigLoaded,
		linkedCloudARIs,
	};
};

export const get3pProductKeys: ({
	cloudId,
	absoluteUrl,
	userId,
	degradationCallback,
	cacheAnalyticsCallback,
	networkCallbacks,
}: TSearch3pConfigProps) => Promise<{
	productKeys: string[];
	thirdPartyConfigs: Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>;
}> = async ({
	cloudId,
	absoluteUrl,
	userId,
	degradationCallback,
	cacheAnalyticsCallback,
	networkCallbacks,
}: TSearch3pConfigProps) => {
	/**
	 * These parameters define a caching strategy where:
	 *  - Cache is refreshed only if the cached data is expired, with a 1 minute TTL
	 *  - Cached value is returned, regardless of whether its expired or not
	 */
	const searchConfigParams = {
		cloudId,
		absoluteUrl,
		userId,
		degradationCallback,
		cacheAnalyticsCallback,
		expiration: 1000 * 60, // 1 minute
		returnExpired: true,
		onlyRefreshIfExpired: true,
		networkCallbacks,
	};

	const resources = await get3pSearchConfig(searchConfigParams);

	return map3pResourcesToProductKeys(resources);
};

const EMPTY_RESPONSE: (ProductKeys3P | ForgeConnectorProductKey)[] = [];

export const useThirdPartyConfig: ({
	cloudId,
	absoluteUrl,
	userId,
	disabled,
	experience,
}: Partial<TSearch3pConfigProps> & {
	experience?: SearchStoreExperience;
}) => {
	productKeys?: (ProductKeys3P | ForgeConnectorProductKey)[];
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
} = ({
	cloudId,
	absoluteUrl,
	userId,
	disabled,
	experience = SearchExperience.SEARCH_PAGE,
}: Partial<TSearch3pConfigProps> & { experience?: SearchStoreExperience } = {}) => {
	const [config, setConfig] = useState<{
		productKeys?: (ProductKeys3P | ForgeConnectorProductKey)[];
		thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	}>({});
	const logException = useLogException();
	const { fireAnalyticsEvent } = useAnalytics({ experience });
	const [searchSessionId] = useSearchSessionId();

	useEffect(() => {
		if (!config.productKeys) {
			if (!disabled && cloudId) {
				const uxTaskId = crypto.randomUUID();
				const taskStartAt = performance.now();

				const baseUXVitalStatsParams = {
					searchSessionId,
					taskId: uxTaskId,
					task: TASK_NAMES.SEARCH_PAGE_THIRD_PARTY_CONFIG_UX,
				};

				// Fire task start for UX tracking
				fireAnalyticsEvent(onTaskStart(baseUXVitalStatsParams));
				// Create network callbacks for tracking network requests
				const configNetworkTaskId = crypto.randomUUID();
				const networkCallbacks = createVitalStatsNetworkCallbacks(
					configNetworkTaskId,
					TASK_NAMES.SEARCH_PAGE_THIRD_PARTY_CONFIG_NETWORK,
					searchSessionId,
					fireAnalyticsEvent,
					taskStartAt,
					{ uxTaskId },
				);

				// DegradedDependency may not be visible to the end-user due to request caching. This is a health metric for our BE
				const degradationCallback: DegradationCallback = (reason) => {
					fireAnalyticsEvent(
						onDegradedDependency({
							dependency: 'search-3p-config',
							searchSessionId,
							reason,
							source: EVENT_SOURCE,
						}),
					);
				};

				const cacheAnalyticsCallback: CacheAnalyticsCallback = async (state) => {
					fireAnalyticsEvent(
						onRequestCacheAccess({
							requestName: 'search-3p-config',
							searchSessionId,
							cacheHit: state.hit,
							refreshTriggered: state.refreshTriggered,
							source: EVENT_SOURCE,
						}),
					);
				};

				get3pProductKeys({
					cloudId,
					absoluteUrl,
					userId,
					degradationCallback,
					cacheAnalyticsCallback,
					networkCallbacks,
				})
					.then((data) => {
						setConfig({
							productKeys: data.productKeys,
							thirdPartyConfigs: data.thirdPartyConfigs,
						});

						// Fire user experience metric - user got data (success from UX perspective)
						fireAnalyticsEvent(
							onTaskSuccess({
								...baseUXVitalStatsParams,
								moreContext: {
									timeTaken: performance.now() - taskStartAt,
								},
							}),
						);
					})
					.catch((error) => {
						// FailedDependency is visible to the end-user. This is a health metric for our UX
						fireAnalyticsEvent(
							onFailedDependency({
								dependency: 'search-3p-config',
								searchSessionId,
								source: EVENT_SOURCE,
							}),
						);

						// Fire user experience metric - user did not get data (failure from UX perspective)
						fireAnalyticsEvent(
							onTaskFail({
								...baseUXVitalStatsParams,
								moreContext: {
									error: error.name,
									timeTaken: performance.now() - taskStartAt,
								},
							}),
						);

						setConfig({
							productKeys: EMPTY_RESPONSE,
							thirdPartyConfigs: {},
						});

						logException(error);
					});
			} else {
				setConfig({
					productKeys: EMPTY_RESPONSE,
					thirdPartyConfigs: {},
				});
			}
		}
	}, [
		cloudId,
		absoluteUrl,
		userId,
		disabled,
		config,
		logException,
		fireAnalyticsEvent,
		searchSessionId,
	]);

	return config;
};
